import { status } from 'adminConstants/investorReporting';
import {
  PropsWithChildren,
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react';
import { Api } from 'further-types/investor-reporting';
import api from 'lib/trpcClient';
import { CalendarDay } from 'further-ui/utils';

type Props = {
  investorReport: Record<string, any> | null;
  isSuperAdmin: boolean;
  userFirmId?: string;
};

type ManagerOverview = {
  managerOverViewTitle: string;
  managerOverViewShortDescription: string;
  managerOverViewProvideCommentary: boolean;
  managerOverViewCommentary: string;
};

type FundInformation = {
  copyFund: boolean;
  title: string;
  shortDescription: string;
  provideCommentary: boolean;
  commentary: string;
};

type CompanyInformation = Api.GetInvesteeListResponse['result'][number];

type InvestorReportingFormContextType = {
  investorReport: Props['investorReport'];
  setInvestorReport: (investorReport: Props['investorReport']) => void;
  firmId?: string;
  submitDate: CalendarDay;
  managerOverview: ManagerOverview;
  fundInformation: Array<FundInformation>;
  companyInformation: Array<CompanyInformation>;
  showFirmDropdown: boolean;
  notifyInvestors: boolean;
  setFirmId: (firmId: string) => void;
  setManagerOverview: (managerOverview: ManagerOverview) => void;
  setFundInformation: (fundInformation: Array<FundInformation>) => void;
  setCompanyInformation: React.Dispatch<
    React.SetStateAction<Array<CompanyInformation>>
  >;
  setSubmitDate: (submitDate: CalendarDay) => void;
  reportStatus: string;
  setNotifyInvestors: (notifyInvestors: boolean) => void;
  setAllActiveFundIds: (allActiveFundIds: Array<string>) => void;
  allActiveFundIds: Array<string>;
  fundTypes: Array<{
    _id: string;
    name: string;
  }>;
  isFetchingFundTypes: boolean;
  selectedFundTypes?: Array<string>;
  setSelectedFundTypes: (selectedFundTypes: Array<string> | undefined) => void;
};

const emptyManagerOverview = {
  managerOverViewTitle: '',
  managerOverViewShortDescription: '',
  managerOverViewProvideCommentary: false,
  managerOverViewCommentary: '',
};

const emptyFundInformation = {
  title: '',
  shortDescription: '',
  copyFund: false,
  provideCommentary: false,
  commentary: '',
};

const InvestorReportingFormContext =
  createContext<InvestorReportingFormContextType>({
    submitDate: new CalendarDay(),
    investorReport: null,
    setInvestorReport: () => undefined,
    showFirmDropdown: false,
    notifyInvestors: false,
    managerOverview: emptyManagerOverview,
    fundInformation: [],
    companyInformation: [],
    reportStatus: status.DRAFT,
    setFirmId: () => undefined,
    setManagerOverview: () => undefined,
    setFundInformation: () => undefined,
    setCompanyInformation: () => undefined,
    setSubmitDate: () => undefined,
    setNotifyInvestors: () => undefined,
    setAllActiveFundIds: () => undefined,
    allActiveFundIds: [],
    fundTypes: [],
    isFetchingFundTypes: false,
    selectedFundTypes: [],
    setSelectedFundTypes: () => undefined,
  });

const getInitialCompanyUpdates = (investeeUpdates: Array<any>) => {
  investeeUpdates?.forEach((singleCompany) => {
    if (!singleCompany.copyFromCompanies) {
      singleCompany.copyFromCompanies = [];
    }
    if (typeof singleCompany.updatedSharePrice === 'number') {
      singleCompany.updatedSharePrice =
        singleCompany.updatedSharePrice.toString();
    }
  });
  return investeeUpdates;
};

const InvestorReportingFormContextProvider: React.FC<
  PropsWithChildren<Props>
> = ({
  children,
  investorReport: initialInvestorReport,
  userFirmId,
  isSuperAdmin,
}) => {
  const [investorReport, setInvestorReport] = useState(initialInvestorReport);
  const [firmId, setFirmId] = useState(investorReport?.firm?._id ?? userFirmId);
  const [submitDate, setSubmitDate] = useState<CalendarDay>(
    new CalendarDay(
      investorReport?.submitDate
        ? new Date(investorReport.submitDate).toISOString().slice(0, 10)
        : new Date(),
    ),
  );
  const [managerOverview, setManagerOverview] = useState<ManagerOverview>({
    managerOverViewProvideCommentary:
      !!investorReport?.managerOverViewProvideCommentary,
    managerOverViewTitle: investorReport?.managerOverViewTitle ?? '',
    managerOverViewShortDescription:
      investorReport?.managerOverViewShortDescription ?? '',
    managerOverViewCommentary: investorReport?.managerOverViewCommentary ?? '',
  });
  const [fundInformation, setFundInformation] = useState<
    InvestorReportingFormContextType['fundInformation']
  >([]);

  const [companyInformation, setCompanyInformation] = useState<
    InvestorReportingFormContextType['companyInformation']
  >(getInitialCompanyUpdates(investorReport?.investeeUpdates ?? []));

  const reportStatus = investorReport?.status ?? status.DRAFT;
  const [notifyInvestors, setNotifyInvestors] = useState(false);
  const [allActiveFundIds, setAllActiveFundIds] = useState<Array<string>>([]);

  const processFundInformation = (
    updatedFundInformation: Array<FundInformation>,
  ) =>
    fundInformation.map((fund, index) => {
      return updatedFundInformation[index].copyFund
        ? {
            ...fund,
            ...emptyFundInformation,
            copyFund: true,
          }
        : {
            ...fund,
            ...updatedFundInformation[index],
          };
    });

  useEffect(() => {
    if (investorReport?.fundUpdates) {
      setFundInformation(
        investorReport.fundUpdates.map((fund) => ({
          ...fund,
          copyFund: fund.copyFund ?? false,
          title: fund.title ?? '',
          shortDescription: fund.shortDescription ?? '',
          provideCommentary: fund.provideCommentary ?? false,
          commentary: fund.commentary ?? '',
        })),
      );
    }
  }, [investorReport?.fundUpdates]);

  const [selectedFundTypes, setSelectedFundTypes] = useState<
    Array<string> | undefined
  >(undefined);
  const { data: fundTypes, isFetching: isFetchingFundTypes } =
    api.eisFirm.getFundTypes.useQuery({ firmId }, { enabled: !!firmId });

  useEffect(() => {
    if (!investorReport && fundTypes) {
      setSelectedFundTypes(fundTypes.map((fundType) => fundType._id));
    }
  }, [investorReport, fundTypes]);

  useEffect(() => {
    if (investorReport?.fundTypes) {
      setSelectedFundTypes(investorReport.fundTypes);
    }
  }, [investorReport?.fundTypes]);

  const contextValue = {
    investorReport,
    setInvestorReport,
    firmId,
    setFirmId,
    submitDate,
    setSubmitDate,
    notifyInvestors,
    showFirmDropdown: isSuperAdmin && !investorReport?._id,
    managerOverview,
    setManagerOverview,
    fundInformation,
    setFundInformation,
    processFundInformation,
    companyInformation,
    setCompanyInformation,
    reportStatus,
    setNotifyInvestors,
    setAllActiveFundIds,
    allActiveFundIds,
    fundTypes: fundTypes ?? [],
    isFetchingFundTypes,
    selectedFundTypes,
    setSelectedFundTypes,
  };

  return (
    <InvestorReportingFormContext.Provider value={contextValue}>
      {children}
    </InvestorReportingFormContext.Provider>
  );
};

export const useInvestorReportingFormContext = () =>
  useContext(InvestorReportingFormContext);

export default InvestorReportingFormContextProvider;
